import {
  CustomTransactionItemsErrorResponse,
  ForbiddenCodeEnum,
  isAuthError,
  isForbiddenError,
  isInvalidError,
  isSuccess,
} from '@/context/services/type';
import { TransactionItemsColumn } from '@/features/transaction_items/List/type';
import { useQueryClient } from '@tanstack/react-query';
import {
  ApInvoiceTransactionGeneralInput,
  ApInvoiceTransactionGeneralInputComponentType,
  ApInvoiceTransactionGeneralInputDateFormatType,
  CommonError,
  getGetApInvoiceTransactionGeneralInputQueryKey,
  getGetApInvoiceTransactionGeneralInputsQueryKey,
  PostApInvoiceTransactionGeneralInputsBodyApInvoiceTransactionGeneralInputComponentType,
  UnauthorizedResponse,
  useBulkUpdateInvoiceTransactionItems,
  useDeleteApInvoiceTransactionGeneralInput,
  useGetApInvoiceTransactionGeneralInputsSuspense,
  usePostApInvoiceTransactionGeneralInputs,
  usePutApInvoiceTransactionGeneralInput,
  useReorderApInvoiceTransactionGeneralInputs,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';

type FetchTransactionItems = (arg: {
  report_type_id: string;
}) => FetchTransactionItemsResult;

export type FetchTransactionItemsResult = {
  data: TransactionItemsColumn[];
  isLoadingGetTransactionItems: boolean;
  refetch: () => Promise<TransactionItemsColumn[]>;
};

export const TransactionItemComponentType =
  ApInvoiceTransactionGeneralInputComponentType;

export type TransactionItemDateFormatType =
  ApInvoiceTransactionGeneralInputDateFormatType;

export type CreateApInvoiceTransactionGeneralInputParams = {
  ap_invoice_transaction_general_input: ApInvoiceTransactionGeneralInput;
};

export type ComponentTypeForPost =
  PostApInvoiceTransactionGeneralInputsBodyApInvoiceTransactionGeneralInputComponentType;

type TCreateTransactionItem = ReturnType<
  typeof usePostApInvoiceTransactionGeneralInputs
>['mutateAsync'];
type CreateTransactionItemMutateArgs = Parameters<TCreateTransactionItem>['0'];

type TUpdateTransactionItem = ReturnType<
  typeof usePutApInvoiceTransactionGeneralInput
>['mutateAsync'];
export type UpdateTransactionItemMutateArgs =
  Parameters<TUpdateTransactionItem>['0'];

type TDeleteTransactionItem = ReturnType<
  typeof useDeleteApInvoiceTransactionGeneralInput
>['mutateAsync'];
type DeleteTransactionItemMutateArgs = Parameters<TDeleteTransactionItem>['0'];

type TReorderTransactionItems = ReturnType<
  typeof useReorderApInvoiceTransactionGeneralInputs
>['mutateAsync'];
type ReorderTransactionItemsMutateArgs =
  Parameters<TReorderTransactionItems>['0'];

type TBulkUpdateTransactionItems = ReturnType<
  typeof useBulkUpdateInvoiceTransactionItems
>['mutateAsync'];
export type BulkUpdateTransactionItemsMutateArgs =
  Parameters<TBulkUpdateTransactionItems>['0'];

type CreateApInvoiceTransactionGeneralInputResult =
  | ApInvoiceTransactionGeneralInputSuccess
  | ApInvoiceTransactionGeneralInputCommonError
  | ApInvoiceTransactionGeneralInputInvalidError
  | ApInvoiceTransactionGeneralInputAuthError;

type UpdateApInvoiceTransactionGeneralInputResult =
  | ApInvoiceTransactionGeneralInputSuccess
  | ApInvoiceTransactionGeneralInputCommonError
  | ApInvoiceTransactionGeneralInputInvalidError
  | ApInvoiceTransactionGeneralInputAuthError;

type DeleteApInvoiceTransactionGeneralInputResult =
  | ApInvoiceTransactionGeneralInputSuccess
  | ApInvoiceTransactionGeneralInputCommonError
  | ApInvoiceTransactionGeneralInputAuthError;

type ReorderApInvoiceTransactionGeneralInputResult =
  | ApInvoiceTransactionGeneralInputSuccess
  | ApInvoiceTransactionGeneralInputCommonError
  | ApInvoiceTransactionGeneralInputInvalidError
  | ApInvoiceTransactionGeneralInputAuthError
  | ApInvoiceTransactionGeneralInputProxyLoginError;

export type ApInvoiceTransactionGeneralInputUnprocessableEntityResponse = {
  errors: {
    [key: string]: {
      message: string;
      type: string;
    };
  };
};
export enum ApInvoiceTransactionStatusEnum {
  SUCCESS = 'success',
  AUTH_ERROR = 'authError',
  INVALID_ERROR = 'invalidError',
  COMMON_ERROR = 'commonError',
  PROXY_LOGIN_ERROR = 'proxyLoginError',
}

export type ApInvoiceTransactionGeneralInputSuccess = {
  status: ApInvoiceTransactionStatusEnum.SUCCESS;
};
export type ApInvoiceTransactionGeneralInputCommonError = {
  status: ApInvoiceTransactionStatusEnum.COMMON_ERROR;
  errors: CommonError;
};
export type ApInvoiceTransactionGeneralInputInvalidError = {
  status: ApInvoiceTransactionStatusEnum.INVALID_ERROR;
  errors: ApInvoiceTransactionGeneralInputUnprocessableEntityResponse;
};
export type ApInvoiceTransactionGeneralInputProxyLoginError = {
  status: ApInvoiceTransactionStatusEnum.PROXY_LOGIN_ERROR;
  errors: CommonError;
};
export type ApInvoiceTransactionGeneralInputAuthError = {
  status: ApInvoiceTransactionStatusEnum.AUTH_ERROR;
  errors: UnauthorizedResponse;
  redirectPath: string;
};

export const useGetTransactionItems: FetchTransactionItems = (arg) => {
  const {
    data,
    isFetching,
    refetch: _refetch,
  } = useGetApInvoiceTransactionGeneralInputsSuspense(arg.report_type_id);
  const refetch = useCallback(async () => {
    const { data } = await _refetch();
    return (
      data?.data.ap_invoice_transaction_general_inputs?.map(responseToColumn) ??
      []
    );
  }, [_refetch]);

  if (data && (isAuthError(data.status) || isForbiddenError(data.status))) {
    location.href = data.headers?.location ?? '/';
  }

  return {
    data:
      data.data.ap_invoice_transaction_general_inputs?.map(responseToColumn) ??
      [],
    isLoadingGetTransactionItems: isFetching,
    refetch,
  };
};

const responseToColumn = (
  item: ApInvoiceTransactionGeneralInput
): TransactionItemsColumn => {
  return {
    id: item.id.toString(),
    label: item.label,
    type: item.type,
    component_type: item.component_type,
    ref_label: item.ref_label,
    ref_link: item.ref_link,
    is_visible: item.is_visible,
    priority: item.priority ?? 0,
    system_tooltip: item?.system_tooltip,
    date_format_type: item?.date_format_type,
    can_delete: item.can_delete,
    delete_tooltip: item.delete_tooltip ?? undefined,
  };
};

export const useCreateTransactionItem = (report_type_id: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } =
    usePostApInvoiceTransactionGeneralInputs();

  const mutateAsync = useCallback(
    async (
      args: CreateTransactionItemMutateArgs
    ): Promise<CreateApInvoiceTransactionGeneralInputResult> => {
      const resp = await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        // Only invalidate queries on actual success
        query.invalidateQueries({
          queryKey:
            getGetApInvoiceTransactionGeneralInputsQueryKey(report_type_id),
        });
        return {
          status: ApInvoiceTransactionStatusEnum.SUCCESS,
        };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.AUTH_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as UnauthorizedResponse,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else if (isInvalidError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.INVALID_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ??
            {}) as ApInvoiceTransactionGeneralInputUnprocessableEntityResponse,
        };
      } else {
        return {
          status: ApInvoiceTransactionStatusEnum.COMMON_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      }
    },
    [_mutateAsync, query, report_type_id]
  );
  return { mutateAsync };
};

export const useUpdateTransactionItem = (report_type_id: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } =
    usePutApInvoiceTransactionGeneralInput();

  const mutateAsync = useCallback(
    async (
      args: UpdateTransactionItemMutateArgs
    ): Promise<UpdateApInvoiceTransactionGeneralInputResult> => {
      const resp = await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        // Only invalidate queries on actual success
        query.invalidateQueries({
          queryKey:
            getGetApInvoiceTransactionGeneralInputsQueryKey(report_type_id),
        });
        // Also invalidate the detail query for the specific item
        query.invalidateQueries({
          queryKey: getGetApInvoiceTransactionGeneralInputQueryKey(args.id),
        });
        return {
          status: ApInvoiceTransactionStatusEnum.SUCCESS,
        };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.AUTH_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as UnauthorizedResponse,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else if (isInvalidError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.INVALID_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ??
            {}) as ApInvoiceTransactionGeneralInputUnprocessableEntityResponse,
        };
      } else {
        return {
          status: ApInvoiceTransactionStatusEnum.COMMON_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      }
    },
    [_mutateAsync, query, report_type_id]
  );
  return { mutateAsync };
};

export const useDeleteTransactionItem = (report_type_id: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } =
    useDeleteApInvoiceTransactionGeneralInput();

  const mutateAsync = useCallback(
    async (
      args: DeleteTransactionItemMutateArgs
    ): Promise<DeleteApInvoiceTransactionGeneralInputResult> => {
      const resp = await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        // Only invalidate queries on actual success
        query.invalidateQueries({
          queryKey:
            getGetApInvoiceTransactionGeneralInputsQueryKey(report_type_id),
        });
        return {
          status: ApInvoiceTransactionStatusEnum.SUCCESS,
        };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.AUTH_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp ?? {}) as UnauthorizedResponse,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else {
        return {
          status: ApInvoiceTransactionStatusEnum.COMMON_ERROR,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp ?? {}) as CommonError,
        };
      }
    },
    [_mutateAsync, query, report_type_id]
  );
  return { mutateAsync };
};

type TReorderApInvoiceTransactionGeneralInputsResponse =
  AxiosResponse<void | CustomTransactionItemsErrorResponse>;

export const useUpdateReorderTransactionItem = (report_type_id: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } =
    useReorderApInvoiceTransactionGeneralInputs();

  const mutateAsync = useCallback(
    async (
      args: ReorderTransactionItemsMutateArgs
    ): Promise<ReorderApInvoiceTransactionGeneralInputResult> => {
      const resp: TReorderApInvoiceTransactionGeneralInputsResponse =
        await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        // Only invalidate queries on actual success
        query.invalidateQueries({
          queryKey:
            getGetApInvoiceTransactionGeneralInputsQueryKey(report_type_id),
        });
        return {
          status: ApInvoiceTransactionStatusEnum.SUCCESS,
        };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        if (resp.data?.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
          return {
            status: ApInvoiceTransactionStatusEnum.PROXY_LOGIN_ERROR,
            // @ts-ignore - resp.data is void for 204 but exists for error responses
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            errors: (resp.data ?? {}) as CommonError,
          };
        }
        return {
          status: ApInvoiceTransactionStatusEnum.AUTH_ERROR,
          // @ts-ignore - resp.data is void for 204 but exists for error responses
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as UnauthorizedResponse,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else if (isInvalidError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.INVALID_ERROR,
          // @ts-ignore - resp.data is void for 204 but exists for error responses
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ??
            {}) as ApInvoiceTransactionGeneralInputUnprocessableEntityResponse,
        };
      } else {
        return {
          status: ApInvoiceTransactionStatusEnum.COMMON_ERROR,
          // @ts-ignore - resp.data is void for 204 but exists for error responses
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      }
    },
    [_mutateAsync, query, report_type_id]
  );
  return { mutateAsync };
};

type TBulkUpdateInvoiceTransactionItemsResponse =
  AxiosResponse<void | CustomTransactionItemsErrorResponse>;

export const useBulkUpdateReorderTransactionItem = (report_type_id: string) => {
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } = useBulkUpdateInvoiceTransactionItems();

  const mutateAsync = useCallback(
    async (
      args: BulkUpdateTransactionItemsMutateArgs
    ): Promise<ReorderApInvoiceTransactionGeneralInputResult> => {
      const resp: TBulkUpdateInvoiceTransactionItemsResponse =
        await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        // Only invalidate queries on actual success
        query.invalidateQueries({
          queryKey:
            getGetApInvoiceTransactionGeneralInputsQueryKey(report_type_id),
        });
        return {
          status: ApInvoiceTransactionStatusEnum.SUCCESS,
        };
      } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
        if (resp.data?.code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE) {
          return {
            status: ApInvoiceTransactionStatusEnum.PROXY_LOGIN_ERROR,
            // @ts-ignore - resp.data is void for 204 but exists for error responses
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            errors: (resp.data ?? {}) as CommonError,
          };
        }
        return {
          status: ApInvoiceTransactionStatusEnum.AUTH_ERROR,
          // @ts-ignore - resp.data is void for 204 but exists for error responses
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as UnauthorizedResponse,
          redirectPath: resp.headers?.location ?? '/',
        };
      } else if (isInvalidError(resp.status)) {
        return {
          status: ApInvoiceTransactionStatusEnum.INVALID_ERROR,
          // @ts-ignore - resp.data is void for 204 but exists for error responses
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ??
            {}) as ApInvoiceTransactionGeneralInputUnprocessableEntityResponse,
        };
      } else {
        return {
          status: ApInvoiceTransactionStatusEnum.COMMON_ERROR,
          // @ts-ignore - resp.data is void for 204 but exists for error responses
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: (resp.data ?? {}) as CommonError,
        };
      }
    },
    [_mutateAsync, query, report_type_id]
  );
  return { mutateAsync };
};
